import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import {
  getVisibilityScore,
  getVisibilityScoreHistory,
  getUavsInsights,
  getScoreEvents,
  getUavsFixes,
} from '../services/uavsService';
import { getVisitorsAttribution } from '../services/visitorsService';
import { BusinessContext } from '../contexts/BusinessContext';
import { PILLAR_ORDER } from '../components/uavs/uavsMeta';
import TopPriorities from '../components/uavs/TopPriorities';
import FixesDoneCard from '../components/uavs/FixesDoneCard';
import PillarCard from '../components/uavs/PillarCard';
import AnnotatedTrendline from '../components/uavs/AnnotatedTrendline';
import EventTimeline from '../components/uavs/EventTimeline';
import AIPresenceBreakdown from '../components/uavs/AIPresenceBreakdown';
import PromptEngineMatrix from '../components/uavs/PromptEngineMatrix';
import RecentChanges from '../components/uavs/RecentChanges';
import CompetitorBenchmarkCard from '../components/uavs/CompetitorBenchmarkCard';
import {
  useTokens,
  GradientHeroCard,
  MetricBarCards,
} from '../components/primitives';

const fmtUsdCents = (cents) =>
  typeof cents === 'number' && cents > 0
    ? `$${Math.round(cents / 100).toLocaleString()}`
    : '$0';

export default function UavsScore() {
  const t = useTokens();
  const [scoreData, setScoreData] = useState(null);
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [events, setEvents] = useState([]);
  const [attribution, setAttribution] = useState(null);
  const [fixes, setFixes] = useState([]);
  const [loading, setLoading] = useState(true);

  const { business } = useContext(BusinessContext);
  // Done-for-you implementation (the AI fix loop) is a The Ryzze benefit,
  // resolved server-side and surfaced on the business payload (entitlement
  // layer). Lower tiers see the gaps + a nudge, never the active fix action.
  const canAutoFix = Boolean(business?.entitlements?.doneForYouImplementation);

  const reloadFixes = useCallback(() => {
    getUavsFixes({ limit: 50 })
      .then((r) => setFixes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      getVisibilityScore()
        .then((r) => r.data)
        .catch(() => null),
      getVisibilityScoreHistory({ limit: 10 })
        .then((r) => r.data)
        .catch(() => []),
      getUavsInsights()
        .then((r) => r.data)
        .catch(() => null),
      getScoreEvents({ limit: 50 })
        .then((r) => r.data)
        .catch(() => []),
      getVisitorsAttribution(30).catch(() => null),
      getUavsFixes({ limit: 50 })
        .then((r) => r.data)
        .catch(() => []),
    ]).then(([score, hist, ins, evts, attr, fx]) => {
      setScoreData(score);
      setHistory(Array.isArray(hist) ? hist : []);
      setInsights(ins);
      setEvents(Array.isArray(evts) ? evts : []);
      setAttribution(attr);
      setFixes(Array.isArray(fx) ? fx : []);
      setLoading(false);
    });
  }, []);

  // KPI strip (design Page 12): citations, leads-from-AI (the Analytics
  // cross-link), and prompts tracked. Score itself stays the gradient hero.
  const kpiCards = useMemo(() => {
    const ap = insights?.aiPresence;
    if (!ap) return [];
    const cited = ap.citedAsSource?.cited ?? 0;
    const prompts = ap.matrix?.prompts?.length ?? 0;
    const engines = ap.matrix?.engines?.length ?? 0;
    const fromAi = attribution?.fromAI || { customers: 0, revenueCents: 0 };
    return [
      { label: 'Citations', value: cited, bar: 'green' },
      {
        label: 'Leads from AI',
        value: `${fromAi.customers || 0}`,
        sub: fromAi.engines?.[0]
          ? `${fmtUsdCents(fromAi.revenueCents)} · most from ${fromAi.engines[0].label}`
          : fmtUsdCents(fromAi.revenueCents),
        gradient: true,
      },
      {
        label: 'Prompts tracked',
        value: prompts,
        bar: 'purple',
      },
      {
        label: 'Engines',
        value: engines,
        bar: 'blue',
      },
    ];
  }, [insights, attribution]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!scoreData || scoreData.score == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 900,
            letterSpacing: '-0.6px',
            mb: 1,
            color: t.text,
          }}
        >
          AI Visibility
        </Typography>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Your first UAVS scan will run automatically based on your subscription
          schedule. Check back soon.
        </Alert>
      </Box>
    );
  }

  const includedPillars = scoreData.pillarsIncluded || [];
  const orderedPillars = PILLAR_ORDER.map((key) => [
    key,
    scoreData.pillarScores?.[key],
  ]).filter(([, p]) => p && p.max > 0);

  const guidanceLevel =
    scoreData.score >= 70
      ? 'success'
      : scoreData.score >= 40
        ? 'info'
        : 'warning';
  const guidanceTitle =
    scoreData.score >= 70
      ? 'Strong AI Visibility'
      : scoreData.score >= 40
        ? 'Building Momentum'
        : 'Room to Grow';
  const guidanceMsg =
    scoreData.score >= 70
      ? 'Your site is well-positioned for AI search visibility. Keep maintaining your content and structured data.'
      : scoreData.score >= 40
        ? 'Your site has a foundation but there are opportunities to improve how AI systems understand and recommend you.'
        : 'AI systems are likely having difficulty finding, understanding, or recommending your business. Focus on the priorities below.';

  const evidenceByCheck = insights?.siteHealthEvidence || {};

  return (
    <Box sx={{ p: 3, maxWidth: 1080, mx: 'auto' }}>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 900,
          letterSpacing: '-0.6px',
          mb: 0.5,
          color: t.text,
        }}
      >
        AI Visibility
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: t.muted, mb: 3 }}>
        How well AI search systems can find, understand, and recommend your
        business.
      </Typography>

      <GradientHeroCard sx={{ p: 4, mb: 3, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 56,
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-2px',
            }}
          >
            {scoreData.score}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800, opacity: 0.7 }}>
            /100
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: 12.5, opacity: 0.85, mt: 1, fontWeight: 600 }}
        >
          UAVS Visibility Score
        </Typography>
        {scoreData.grade ? (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mt: 1.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: 'rgba(255,255,255,.18)',
              border: '1px solid rgba(255,255,255,.3)',
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Grade {scoreData.grade}
          </Box>
        ) : null}
      </GradientHeroCard>

      {kpiCards.length ? (
        <MetricBarCards cards={kpiCards} sx={{ mb: 3 }} />
      ) : null}

      {history.length >= 2 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <AnnotatedTrendline history={history} events={events} />
        </Box>
      )}

      <Alert severity={guidanceLevel} sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {guidanceTitle}
        </Typography>
        <Typography variant="body2">{guidanceMsg}</Typography>
      </Alert>

      <CompetitorBenchmarkCard benchmark={insights?.competitorBenchmark} />

      <TopPriorities
        pillarScores={scoreData.pillarScores}
        canAutoFix={canAutoFix}
        onFixApplied={reloadFixes}
      />

      <FixesDoneCard fixes={fixes} />

      <RecentChanges history={history} />

      <EventTimeline events={events} />

      {orderedPillars.map(([key, pillar]) => (
        <PillarCard
          key={key}
          pillarKey={key}
          pillar={pillar}
          isLocked={!includedPillars.includes(key)}
          evidenceByCheck={evidenceByCheck}
        />
      ))}

      {includedPillars.includes('ai_presence') &&
        insights?.aiPresence?.matrix && (
          <PromptEngineMatrix matrix={insights.aiPresence.matrix} />
        )}

      {includedPillars.includes('ai_presence') && insights?.aiPresence && (
        <AIPresenceBreakdown aiPresence={insights.aiPresence} />
      )}
    </Box>
  );
}
